import {Component, DestroyRef, effect, inject, OnInit, viewChildren} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MatDialog} from '@angular/material/dialog';
import moment from 'moment';
import {GridModel} from 'src/app/shared/model/page-grid';
import {WidgetInfoItem} from 'src/app/dashboard/model/widget-info-item';
import {AutoUnsubscribe} from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import {PageGridComponent} from 'src/app/shared/components/page-grid/page-grid.component';
import {AdxSbDailyReconciliation} from '../model/adx-sb-daily-reconciliation';
import {AdxSbInvoiceBreakdown} from '../model/adx-sb-invoice-breakdown';
import {AdxSbBookingMatch} from '../model/adx-sb-booking-match';
import {AdxSbDailyGridService} from '../service/adx-sb-daily-grid.service';
import {AdxSbInvoiceGridService} from '../service/adx-sb-invoice-grid.service';
import {AdxSbBookingGridService} from '../service/adx-sb-booking-grid.service';
import {AdxSbReconciliationService} from '../service/adx-sb-reconciliation.service';
import {AdxSbReconciliationFilterService} from '../service/adx-sb-reconciliation-filter.service';
import {AdxSbFilterModalComponent} from '../components/adx-sb-filter-modal/adx-sb-filter-modal.component';

@Component({
  selector: 'adx-sb-reconciliation',
  templateUrl: './adx-sb-reconciliation.component.html',
  standalone: false
})
@AutoUnsubscribe()
export class AdxSbReconciliationComponent implements OnInit {

  private readonly reconciliationService = inject(AdxSbReconciliationService);
  private readonly filterService = inject(AdxSbReconciliationFilterService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  /** The reconciliation grids, refreshed together when the page filter changes. */
  private readonly grids = viewChildren(PageGridComponent);

  protected kpis: WidgetInfoItem[] = [];

  constructor() {
    // Open the shared filter modal when the navbar's top-right filter button is clicked.
    effect(() => {
      if (!this.filterService.toggleModal()) return;
      this.openFilters();
    });

    // Re-fetch every grid and the KPIs when the page filter is applied/reset.
    effect(() => {
      const filters = this.filterService.filtersValue();
      if (!filters.ReloadGrid) return;
      this.grids().forEach(g => g.getItems());
      this.loadKpis();
    });
  }

  private openFilters(): void {
    const dialog = this.dialog.open(AdxSbFilterModalComponent, {width: '700px'});
    dialog.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.filterService.toggleModal.set(false));
  }

  protected readonly dailyGrid: GridModel<AdxSbDailyReconciliation> = {
    gridDataTestId: 'adx-sb-daily',
    gridTitle: 'Daily Reconciliation (SB vs HTTP)',
    replayButton: true,
    isTotalsRowHidden: true,
    defaultSort: {direction: 'asc', active: 'day'},
    columns: [
      {
        columnProperty: 'day',
        columnHeader: 'Day',
        sort: true,
        textLeft: true,
        processData: (v: string) => v ? moment(v).format('ddd, MMM D') : '',
      },
      {
        columnProperty: 'status',
        columnHeader: 'Status',
        sort: true,
        style: (i: AdxSbDailyReconciliation) => i.status === 'MISMATCH'
          ? {color: '#b00020', 'font-weight': '600'}
          : {color: '#00593f', 'font-weight': '600'},
      },
      {columnProperty: 'sbInvoices', columnHeader: 'SB Inv', sort: true},
      {columnProperty: 'httpInvoices', columnHeader: 'HTTP Inv', sort: true},
      {columnProperty: 'dInvoices', columnHeader: 'Δ Inv', style: this.deltaStyle('dInvoices')},
      {columnProperty: 'sbTrips', columnHeader: 'SB Trips'},
      {columnProperty: 'httpTrips', columnHeader: 'HTTP Trips'},
      {columnProperty: 'dTrips', columnHeader: 'Δ Trips', style: this.deltaStyle('dTrips')},
      {columnProperty: 'sbLocators', columnHeader: 'SB Loc'},
      {columnProperty: 'httpLocators', columnHeader: 'HTTP Loc'},
      {columnProperty: 'dLocators', columnHeader: 'Δ Loc', style: this.deltaStyle('dLocators')},
      {columnProperty: 'sbBookings', columnHeader: 'SB Bk'},
      {columnProperty: 'httpBookings', columnHeader: 'HTTP Bk'},
      {columnProperty: 'dBookings', columnHeader: 'Δ Bk', style: this.deltaStyle('dBookings')},
      {columnProperty: 'sbCommission', columnHeader: 'SB Comm', processData: this.currency},
      {columnProperty: 'httpCommission', columnHeader: 'HTTP Comm', processData: this.currency},
      {columnProperty: 'dCommission', columnHeader: 'Δ Comm', processData: this.currency, style: this.deltaStyle('dCommission')},
      {columnProperty: 'sbTotalFare', columnHeader: 'SB Fare', processData: this.currency},
      {columnProperty: 'httpTotalFare', columnHeader: 'HTTP Fare', processData: this.currency},
      {columnProperty: 'dTotalFare', columnHeader: 'Δ Fare', processData: this.currency, style: this.deltaStyle('dTotalFare')},
      {columnProperty: 'mismatchOn', columnHeader: 'Mismatch On', textLeft: true, wrapContent: true},
    ],
    rowStyle: (i: AdxSbDailyReconciliation): Record<string, string> => i.status === 'MISMATCH' ? {'background-color': '#fdecea'} : {},
    gridGetService: inject(AdxSbDailyGridService),
  };

  /**
   * Per-invoice breakdown: every AdxInvoice listed individually (SB and HTTP),
   * so a single record locator's multiple invoices are all visible with their
   * invoice group. Most useful when filtered by a Record Locator.
   */
  protected readonly breakdownGrid: GridModel<AdxSbInvoiceBreakdown> = {
    gridDataTestId: 'adx-sb-invoices',
    gridTitle: 'Invoice Breakdown (every invoice, SB & HTTP)',
    replayButton: true,
    isTotalsRowHidden: true,
    defaultSort: {direction: 'asc', active: 'recordLocator'},
    columns: [
      {columnProperty: 'recordLocator', columnHeader: 'Record Locator', sort: true, textLeft: true},
      {
        columnProperty: 'path',
        columnHeader: 'Path',
        sort: true,
        style: (i: AdxSbInvoiceBreakdown): Record<string, string> => i.path === 'SB'
          ? {color: '#00593f', 'font-weight': '600'}
          : {color: '#0078d4', 'font-weight': '600'},
      },
      {
        columnProperty: 'adxInvoiceId',
        columnHeader: 'Invoice #',
        sort: true,
        clickableWithItem: (i: AdxSbInvoiceBreakdown) => !!i.adxInvoiceId,
        disablePreventEventLink: true,
        linkStyle: () => ({color: '#0078d4', 'text-decoration': 'underline', cursor: 'pointer'}),
        linkEventFunction: (i: AdxSbInvoiceBreakdown) => `AdxInvoices/${i.adxInvoiceId}`,
      },
      {columnProperty: 'invoiceGroup', columnHeader: 'Invoice Group', sort: true, textLeft: true, wrapContent: true},
      {columnProperty: 'invoiceTypeCode', columnHeader: 'Type', textLeft: true},
      {columnProperty: 'branchName', columnHeader: 'Branch', textLeft: true, wrapContent: true},
      {columnProperty: 'clientName', columnHeader: 'Client', textLeft: true, wrapContent: true},
      {
        columnProperty: 'issueDate',
        columnHeader: 'Issue Date',
        sort: true,
        textLeft: true,
        processData: (v: string) => v ? moment(v).format('MMM D, YYYY') : '',
      },
      {columnProperty: 'bookings', columnHeader: 'Bookings', sort: true},
      {columnProperty: 'commission', columnHeader: 'Commission', processData: this.currency},
      {columnProperty: 'totalAmount', columnHeader: 'Total', processData: this.currency},
      {
        columnProperty: 'sbMessageCount',
        columnHeader: 'SB Msgs',
        style: (i: AdxSbInvoiceBreakdown): Record<string, string> => i.sbMessageCount > 1 ? {color: '#b00020', 'font-weight': '600'} : {},
      },
      {
        columnProperty: 'tripId',
        columnHeader: 'Trip',
        clickableWithItem: (i: AdxSbInvoiceBreakdown) => !!i.tripId,
        disablePreventEventLink: true,
        linkStyle: () => ({color: '#0078d4', 'text-decoration': 'underline', cursor: 'pointer'}),
        linkEventFunction: (i: AdxSbInvoiceBreakdown) => `Trip/${i.tripId}`,
      },
    ],
    rowStyle: (i: AdxSbInvoiceBreakdown): Record<string, string> => i.path === 'SB' ? {'background-color': '#e8f5ee'} : {},
    gridGetService: inject(AdxSbInvoiceGridService),
  };

  /**
   * Booking-level exact-duplicate match. Each booking is identified by its source ADX
   * travel-service id when present; keyless lines (e.g. service fees) fall back to a
   * natural composite. Confirms the SB side reproduced the HTTP side exactly.
   */
  protected readonly bookingGrid: GridModel<AdxSbBookingMatch> = {
    gridDataTestId: 'adx-sb-bookings',
    gridTitle: 'Booking Match (SB vs HTTP, by ADX travel-service id)',
    replayButton: true,
    isTotalsRowHidden: true,
    defaultSort: {direction: 'asc', active: 'recordLocator'},
    columns: [
      {columnProperty: 'recordLocator', columnHeader: 'Record Locator', sort: true, textLeft: true},
      {
        columnProperty: 'matchStatus',
        columnHeader: 'Status',
        sort: true,
        textLeft: true,
        style: (i: AdxSbBookingMatch): Record<string, string> => i.matchStatus === 'MATCHED'
          ? {color: '#00593f', 'font-weight': '600'}
          : {color: '#b00020', 'font-weight': '600'},
      },
      {columnProperty: 'adxTravelServiceId', columnHeader: 'Travel Svc Id', sort: true, textLeft: true},
      {
        columnProperty: 'keyBasis',
        columnHeader: 'Matched By',
        textLeft: true,
        style: (i: AdxSbBookingMatch): Record<string, string> => i.keyBasis === 'Fallback' ? {color: '#8a6d00', 'font-weight': '600'} : {},
      },
      {columnProperty: 'confirmationNumber', columnHeader: 'Confirmation', textLeft: true, wrapContent: true},
      {columnProperty: 'sbBookingCount', columnHeader: 'SB #'},
      {columnProperty: 'httpBookingCount', columnHeader: 'HTTP #'},
      {columnProperty: 'sbTotalFare', columnHeader: 'SB Fare', processData: this.currency},
      {columnProperty: 'httpTotalFare', columnHeader: 'HTTP Fare', processData: this.currency},
      {columnProperty: 'dTotalFare', columnHeader: 'Δ Fare', processData: this.currency, style: this.deltaStyle('dTotalFare')},
      {columnProperty: 'sbCommission', columnHeader: 'SB Comm', processData: this.currency},
      {columnProperty: 'httpCommission', columnHeader: 'HTTP Comm', processData: this.currency},
      {columnProperty: 'dCommission', columnHeader: 'Δ Comm', processData: this.currency, style: this.deltaStyle('dCommission')},
      {
        columnProperty: 'sbAdxInvoiceId',
        columnHeader: 'SB Invoice',
        clickableWithItem: (i: AdxSbBookingMatch) => !!i.sbAdxInvoiceId,
        disablePreventEventLink: true,
        linkStyle: () => ({color: '#0078d4', 'text-decoration': 'underline', cursor: 'pointer'}),
        linkEventFunction: (i: AdxSbBookingMatch) => `AdxInvoices/${i.sbAdxInvoiceId}`,
      },
      {
        columnProperty: 'httpAdxInvoiceId',
        columnHeader: 'HTTP Invoice',
        clickableWithItem: (i: AdxSbBookingMatch) => !!i.httpAdxInvoiceId,
        disablePreventEventLink: true,
        linkStyle: () => ({color: '#0078d4', 'text-decoration': 'underline', cursor: 'pointer'}),
        linkEventFunction: (i: AdxSbBookingMatch) => `AdxInvoices/${i.httpAdxInvoiceId}`,
      },
      {
        columnProperty: 'tripId',
        columnHeader: 'Trip',
        clickableWithItem: (i: AdxSbBookingMatch) => !!i.tripId,
        disablePreventEventLink: true,
        linkStyle: () => ({color: '#0078d4', 'text-decoration': 'underline', cursor: 'pointer'}),
        linkEventFunction: (i: AdxSbBookingMatch) => `Trip/${i.tripId}`,
      },
    ],
    rowStyle: (i: AdxSbBookingMatch): Record<string, string> => i.matchStatus !== 'MATCHED' ? {'background-color': '#fdecea'} : {},
    gridGetService: inject(AdxSbBookingGridService),
  };

  ngOnInit(): void {
    this.loadKpis();
  }

  private loadKpis(): void {
    const filters = this.filterService.filtersValue();
    const from = moment(filters.From ?? moment().startOf('isoWeek')).format('YYYY-MM-DD');
    const to = filters.To ? moment(filters.To).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
    const dateBasis = filters.DateBasis ?? 'Issue';

    this.reconciliationService.getDaily(from, to, dateBasis).subscribe({
      next: (data) => this.buildKpis(data.items ?? []),
      error: () => this.buildKpis([]),
    });
  }

  private buildKpis(rows: AdxSbDailyReconciliation[]): void {
    const total = rows.length;
    const ok = rows.filter(r => r.status === 'OK').length;
    const mismatched = total - ok;
    const commDelta = rows.reduce((sum, r) => sum + (r.dCommission || 0), 0);
    const fareDelta = rows.reduce((sum, r) => sum + (r.dTotalFare || 0), 0);

    this.kpis = [
      {
        title: 'Days Reconciled',
        text: `${ok}/${total}`,
        overLine: 'SB matches HTTP',
        icon: 'fact_check',
        color: total > 0 && ok === total ? 'success' : 'caution',
      },
      {
        title: 'Mismatched Days',
        text: `${mismatched}`,
        overLine: mismatched > 0 ? 'Needs review' : 'All clear',
        icon: 'report_problem',
        color: mismatched > 0 ? 'danger' : 'success',
      },
      {
        title: 'Commission Δ (range)',
        text: `$${commDelta.toFixed(2)}`,
        overLine: 'SB − HTTP',
        icon: 'attach_money',
        color: commDelta !== 0 ? 'danger' : 'success',
      },
      {
        title: 'Total Fare Δ (range)',
        text: `$${fareDelta.toFixed(2)}`,
        overLine: 'SB − HTTP',
        icon: 'show_chart',
        color: fareDelta !== 0 ? 'danger' : 'success',
      },
    ];
  }

  /** Currency formatter for grid cells. Pure (no `this`) so it is safe to pass unbound. */
  private currency(value: string): string {
    const n = Number(value);
    return isNaN(n) ? value : `$${n.toFixed(2)}`;
  }

  /** Returns a style fn that paints a delta cell red when its value is non-zero. */
  private deltaStyle(prop: string): (item: Record<string, number>) => Record<string, string> {
    return (item: Record<string, number>): Record<string, string> =>
      item && item[prop] !== 0 ? {color: '#b00020', 'font-weight': '600'} : {};
  }
}
